import { yupResolver } from '@hookform/resolvers/yup'
import { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'

import {
  type UpdateExpertParams,
  useExpertMutation,
} from '../../../../../../../../services/hooks/experts/mutations/useExpertMutation'
import { getErrorMessage } from '../../../../../../../../utils/getErrorMessage'
import { bankDataSchema } from '../../../../../../../../validators/bankDataSchema'
import { legalPersonSchema } from '../../../../../../../../validators/legalPersonSchema'
import { type FormsStepsValues } from './types/FormsSteps'
import { type ExpertDomain } from '../../../../../../../../types/domain/expert-domain'
import {
  TOAST_SUCCESS_DEFAULT_OPTIONS,
  TOAST_ERROR_DEFAULT_OPTIONS,
} from '../../../../../../../../constants/toast'

export const useLegalStepperContainer = (expert?: ExpertDomain) => {
  const { formatMessage } = useIntl()
  const { mutate, isLoading: isSubmitting } = useExpertMutation(expert!.id)

  const [currentStep, setCurrentStep] = useState<0 | 1>(0)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const schema = useMemo(() => {
    if (currentStep === 0) {
      return legalPersonSchema
    }

    return bankDataSchema
  }, [currentStep])

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: expert?.name,
      phone: expert?.phone,
      email: expert?.email,
      cnpj: expert?.legalData?.cnpj,
      companyName: expert?.legalData?.companyName,
      linkedin: expert?.linkedin,
      addressNumber: expert?.naturalData?.addressNumber,
      birthdate: expert?.naturalData?.birthdate,
      rg: expert?.naturalData?.rg,
      cep: expert?.naturalData?.cep,
      street: expert?.naturalData?.street,
      complement: expert?.naturalData?.complement || '',
      neighborhood: expert?.naturalData?.neighborhood,
      city: expert?.naturalData?.city,
      state: expert?.naturalData?.state,
      bank: expert?.bankData?.bank,
      currentAccount: expert?.bankData?.currentAccount,
      bankBranch: expert?.bankData?.bankBranch,
      pix: expert?.bankData?.pix,
    },
  })

  const handleCancel = () => navigate('/expert/personal-data')

  const handleSubmit = (data: FormsStepsValues) => {
    const formattedData: UpdateExpertParams = {
      email: data.email,
      phone: data.phone,
      name: data.name,
      legalData: {
        cnpj: data.cnpj,
        companyName: data.companyName,
      },
      linkedin: data.linkedin ?? undefined,
      naturalData: {
        addressNumber: data.addressNumber,
        birthdate: data.birthdate,
        rg: data.rg,
        cep: data.cep,
        street: data.street,
        complement: data.complement || '',
        neighborhood: data.neighborhood,
        city: data.city,
        state: data.state,
      },
      bankData: {
        bank: data.bank || '',
        bankBranch: data.bankBranch || '',
        currentAccount: data.currentAccount || '',
        pix: data.pix || '',
      },
    }

    mutate(
      { id: expert!.id, data: formattedData },
      {
        onSuccess() {
          queryClient.invalidateQueries('expert')
          toast.success(
            formatMessage({ id: 'DATA_SUCCESSFULLY_SAVED' }),
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )
          navigate('/expert/personal-data')
        },
        onError(error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  return {
    form,
    currentStep,
    isSubmitting,
    setCurrentStep,
    handleCancel,
    handleSubmit,
  }
}
